 
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate, useOutletContext } from "react-router-dom";
import { IStoreState } from "../../../../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../../../../hooks";
import { PageLoader } from "../../../../../../components/PageLoader/PageLoader";
import { LoadState } from "../../../../../../constants/enums";
import { ControlledCustomSelect } from "../../../../../../components/formsComponents";
import {
  alpha,
  Button,
  Menu,
  MenuItem,
  MenuProps,
  Stack,
  styled,
} from "@mui/material";
import { StandardTableActions } from "../../../../../../components/StandardTableActions/StandardTableActions";
import { StatusRenderer } from "../../../../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { PageTitleBar } from "../../../../../../components/PageTitleBar/PageTitleBar";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { formatText } from "../../../../../../helpers";
import {
  downloadAutomobileAuthorizationPDFAsync,
  downloadAutomobileConsentFormPDFAsync,
  downloadAutomobileLiabilityInsurancePDFAsync,
  downloadAutomobileOcep16ListPDFAsync,
  downloadAutomobileOcep17ListPDFAsync,
  downloadAutomobileOcep28AListPDFAsync,
  downloadAutomobileRetireeDiscountPDFAsync,
  downloadAutomobileSharingFillablePDFAsync,
  fetchAutomobilePolicyFormsListAsync,
  previewAutomobileAuthorizationPDFAsync,
  previewAutomobileConsentFormPDFAsync,
  previewAutomobileLiabilityInsurancePDFAsync,
  previewAutomobileOcep16ListPDFAsync,
  previewAutomobileOcep17ListPDFAsync,
  previewAutomobileOcep28AListPDFAsync,
  previewAutomobileRetireeDiscountPDFAsync,
  previewAutomobileSharingFillablePDFAsync,
  previewAutomobileSupplementaryFormPDFAsync,
} from "../../../../../../redux/automobilePolicyForms/automobilePolicyFormsActions";
import {
  DataTableV2RowRenderType,
  IDataTableV2Props,
} from "../../../../../../components/TableV2/interfaces/IDataTableV2Props";
import { useTableV2State } from "../../../../../../components/TableV2/hooks/useTableV2State";
import { DataTableV2 } from "../../../../../../components/TableV2/DataTableV2";


import { IAutomobilePolicyForm } from "../../../../../../redux/automobilePolicyForms/automobliePolicyForms.types";
import { Add } from "@mui/icons-material";
import {
  downloadAutoInsurancePDFAsync,
  downloadCancellationReinstatementPDFAsync,
  downloadLiabilitySlipsPDFAsync,
  downloadOpcf47RPDFAsync,
  downloadOpcf48PDFAsync,
  downloadOpcf49PDFAsync,
  downloadPolicyIntactAuthorizationPDFAsync,
  downloadSef13DPDFAsync,
  downloadSef13HPDFAsync,
  downloadSef16PDFAsync,
  downloadSef17PDFAsync,
  downloadSef19PDFAsync,
  downloadSef40PDFAsync,
  previewAutoInsurancePDFAsync,
  previewCancellationReinstatementPDFAsync,
  previewLiabilitySlipsPDFAsync,
  previewOpcf47RPDFAsync,
  previewOpcf48PDFAsync,
  previewOpcf49PDFAsync,
  previewPolicyIntactAuthorizationPDFAsync,
  previewSef13DPDFAsync,
  previewSef13HPDFAsync,
  previewSef16PDFAsync,
  previewSef17PDFAsync,
  previewSef19PDFAsync,
  previewSef40PDFAsync,
} from "../../../../../../redux/policyCommon/policyCommonActions";
import { COMMON_STATUS_WITH_ALL } from "../../../../../../constants/constants";
import { PageContainer } from "../../../../../../components/container/PageContainer";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }: any) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: "rgb(55, 65, 81)",
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
    ...theme.applyStyles("dark", {
      color: theme.palette.grey[300],
    }),
  },
}));

const POLICY_FORM_TYPES = [
  { label: "All Forms", value: "all_forms" },
  { label: "Liability Slips", value: "liability_slips" },
  {
    label: "Certificate of Liability Insurance",
    value: "certificate_of_liability_insurance",
  },
  {
    label: "Certificate of Automobile Insurance",
    value: "certificate_of_automobile_insurance",
  },
  {
    label: "Automobile Loss Notice",
    value: "automobile_loss_notice",
  },
  { label: "OPCF-16", value: "OPCF-16" },
  { label: "OPCF-17", value: "OPCF-17" },
  { label: "OPCF-28A", value: "OPCF-28A" },
  { label: "OPCF-48", value: "OPCF-48" },
  { label: "OPCF-49", value: "OPCF-49" },
  { label: "OPCF-47R", value: "OPCF-47R" },
  { label: "SEF-16", value: "SEF-16" },
  { label: "SEF-17", value: "SEF-17" },
  { label: "SEF-19", value: "SEF-19" },
  { label: "SEF-13d", value: "SEF-13d" },
  { label: "SEF-13h", value: "SEF-13h" },
  { label: "SEF-40", value: "SEF-40" },
  { label: "SEF-28A", value: "SEF-28A" },
  { label: "Payment Authorization Form", value: "payment_authorization" },
  { label: " Payment Authorization-Intact", value: "intact_authorization" },
  {
    label: "Cancellation or Reinstatement",
    value: "cancellation_or_reinstatement",
  },
  { label: "Privacy Consent", value: "privacy_consent" },
  { label: "Retiree Discount", value: "retiree_discount" },
  { label: "Ride Sharing", value: "ride_sharing" },
  { label: "Garage Supplement", value: "garage_supplementary" },
  // { label: "Ride Sharing", value: "ride_sharing" },
];

const DOWNLOAD_FOR = [
  "liability_slips",
  "certificate_of_liability_insurance",
  "certificate_of_automobile_insurance",
  "OPCF-16",
  "OPCF-17",
  "OPCF-28A",
  "OPCF-48",
  "OPCF-49",
  "OPCF-47R",
  "SEF-16",
  "SEF-17",
  "SEF-19",
  "SEF-13d",
  "SEF-13h",
  "SEF-40",
  "SEF-28A",
  "payment_authorization",
  "intact_authorization",
  "cancellation_or_reinstatement",
  "privacy_consent",
  "retiree_discount",
  "ride_sharing",
  "garage_supplementary",
];

const PREVIEW_FOR = [
  "liability_slips",
  "certificate_of_liability_insurance",
  "certificate_of_automobile_insurance",
  "OPCF-16",
  "OPCF-17",
  "OPCF-28A",
  "OPCF-48",
  "OPCF-49",
  "OPCF-47R",
  "SEF-16",
  "SEF-17",
  "SEF-19",
  "SEF-13d",
  "SEF-13h",
  "SEF-40",
  "SEF-28A",
  "payment_authorization",
  "intact_authorization",
  "cancellation_or_reinstatement",
  "privacy_consent",
  "retiree_discount",
  "ride_sharing",
  "garage_supplementary",
];

export const AutomobilePolicyForms: React.FC = () => {
  const data = useOutletContext() as any;
  const customerId = data[0];
  const customerPolicyId = data[1];
  const [downloading, setDownloading] = React.useState(false);

  const { list, loading } = useSelector(
    (storeState: IStoreState) =>
      storeState.automobile.policyForms.policyFormsList
  );
  const { customerInfo } = useSelector(
    (storeState: IStoreState) => storeState.automobile.basicDetails
  );
  const [status, setStatus] = React.useState<"ACTIVE" | "INACTIVE" | "-1">(
    "-1"
  );
  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();

  const [policyFormRoot, setPolicyFormRoot] = React.useState("all_forms");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [openPolicyCancel, setOpenPolicyCancel] =
    React.useState<boolean>(false);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // const handlePolicyCancelNextStep = (taskCode: string) => {
  //   navigate(
  //     `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/cancellation/${taskCode}`
  //   );
  // };

  const handleFormTypeClick =
    (value: string, incremental_number?: any) => () => {
      setAnchorEl(null);
      let additionalparms = "";
      if (incremental_number) {
        additionalparms = "/" + incremental_number;
      }

      switch (value) {
        case "liability_slips": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/liability-slips${additionalparms}`
          );

          return;
        }
        case "cancellation_or_reinstatement": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/cancellation-or-reinstatement${additionalparms}`
          );

          return;
        }
        case "certificate_of_liability_insurance": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/liability-insurance${additionalparms}`
          );
          return;
        }
        case "certificate_of_automobile_insurance": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/certificate-of-insurance${additionalparms}`
          );
          return;
        }
        case "automobile_loss_notice": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/loss-notice${additionalparms}`
          );
          return;
        }
        case "OPCF-16": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-16${additionalparms}`
          );

          return;
        }
        case "OPCF-17": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-17${additionalparms}`
          );

          return;
        }
        case "OPCF-48": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-48${additionalparms}`
          );

          return;
        }
        case "OPCF-47R": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-47R${additionalparms}`
          );

          return;
        }
        case "OPCF-49": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-49${additionalparms}`
          );

          return;
        }
        case "SEF-16": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-16${additionalparms}`
          );

          return;
        }
        case "SEF-17": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-17${additionalparms}`
          );

          return;
        }
        case "SEF-19": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-19${additionalparms}`
          );

          return;
        }
        case "SEF-13d": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/SEF-13d${additionalparms}`
          );

          return;
        }
        case "SEF-13h": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/SEF-13h${additionalparms}`
          );

          return;
        }
        case "SEF-40": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-40${additionalparms}`
          );

          return;
        }
        case "SEF-28A": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-28A${additionalparms}`
          );

          return;
        }
        case "OPCF-28A": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-28A${additionalparms}`
          );

          return;
        }
        case "payment_authorization": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/authorization${additionalparms}`
          );

          return;
        }
        case "privacy_consent": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/consent-form${additionalparms}`
          );

          return;
        }
        case "retiree_discount": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/retiree-discount${additionalparms}`
          );

          return;
        }
        case "ride_sharing": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sharing-fillable${additionalparms}`
          );

          return;
        }
        case "garage_supplementary": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/supplementary-form${additionalparms}`
          );

          return;
        }
        case "intact_authorization": {
          navigate(`/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/intact-authorization${additionalparms}`);
        }
      }
    };
  const handleDuplicateFormTypeClick =
    (value: string, incremental_number?: any) => () => {
      setAnchorEl(null);
      let additionalparms = "";
      if (incremental_number) {
        additionalparms = "/" + incremental_number;
      }

      switch (value) {
        case "liability_slips": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/liability-slips/duplicate${additionalparms}`
          );

          return;
        }
        case "cancellation_or_reinstatement": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/cancellation/duplicate${additionalparms}`
          );

          return;
        }
        case "certificate_of_liability_insurance": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/liability-insurance/duplicate${additionalparms}`
          );
          return;
        }
        case "certificate_of_automobile_insurance": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/certificate-of-insurance/duplicate${additionalparms}`
          );
          return;
        }
        case "OPCF-16": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-16/duplicate${additionalparms}`
          );

          return;
        }
        case "OPCF-17": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-17/duplicate${additionalparms}`
          );

          return;
        }
        case "OPCF-48": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-48/duplicate${additionalparms}`
          );

          return;
        }
        case "OPCF-47R": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-47R/duplicate${additionalparms}`
          );

          return;
        }
        case "OPCF-49": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-49/duplicate${additionalparms}`
          );

          return;
        }
        case "SEF-16": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-16/duplicate${additionalparms}`
          );

          return;
        }
        case "SEF-17": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-17/duplicate${additionalparms}`
          );

          return;
        }
        case "SEF-19": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-19/duplicate${additionalparms}`
          );

          return;
        }
        case "SEF-13d": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/SEF-13d/duplicate${additionalparms}`
          );

          return;
        }
        case "SEF-13h": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/SEF-13h/duplicate${additionalparms}`
          );

          return;
        }
        case "SEF-40": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-40/duplicate${additionalparms}`
          );

          return;
        }
        case "SEF-28A": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sef-28A/duplicate${additionalparms}`
          );

          return;
        }

        case "OPCF-28A": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/opcf-28A/duplicate${additionalparms}`
          );

          return;
        }
        case "payment_authorization": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/authorization/duplicate${additionalparms}`
          );

          return;
        }
        case "privacy_consent": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/consent-form/duplicate${additionalparms}`
          );

          return;
        }
        case "retiree_discount": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/retiree-discount/duplicate${additionalparms}`
          );

          return;
        }
        case "ride_sharing": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/sharing-fillable/duplicate${additionalparms}`
          );

          return;
        }
        case "garage_supplementary": {
          navigate(
            `/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/supplementary-form/duplicate${additionalparms}`
          );

          return;
        }
        case "intact_authorization": {
          navigate(`/automobile-policy/${customerId}/policy-forms-tab/${customerPolicyId}/intact-authorization/duplicate${additionalparms}`);
          return;
        }
      }
    };

  const handleDownloadClick = (
    policyFormType: string,
    incremental_number: number
  ) => {
    switch (policyFormType) {
      case "liability_slips": {
        dispatch(
          downloadLiabilitySlipsPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "certificate_of_liability_insurance": {
        dispatch(
          downloadAutomobileLiabilityInsurancePDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "OPCF-16": {
        dispatch(
          downloadAutomobileOcep16ListPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "OPCF-17": {
        dispatch(
          downloadAutomobileOcep17ListPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "OPCF-48": {
        dispatch(
          downloadOpcf48PDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "OPCF-47R": {
        dispatch(
          downloadOpcf47RPDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "OPCF-49": {
        dispatch(
          downloadOpcf49PDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "SEF-16": {
        dispatch(
          downloadSef16PDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "SEF-17": {
        dispatch(
          downloadSef17PDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "SEF-19": {
        dispatch(
          downloadSef19PDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }

      case "SEF-13d": {
        dispatch(
          downloadSef13DPDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "SEF-13h": {
        dispatch(
          downloadSef13HPDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "SEF-40": {
        dispatch(
          downloadSef40PDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "SEF-28A": {
        dispatch(
          downloadAutomobileOcep28AListPDFAsync(
            customerPolicyId,
            incremental_number,
            "sef",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "certificate_of_automobile_insurance": {
        dispatch(
          downloadAutoInsurancePDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "OPCF-28A": {
        dispatch(
          downloadAutomobileOcep28AListPDFAsync(
            customerPolicyId,
            incremental_number,
            "opcf",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "payment_authorization": {
        dispatch(
          downloadAutomobileAuthorizationPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "privacy_consent": {
        dispatch(
          downloadAutomobileConsentFormPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "retiree_discount": {
        dispatch(
          downloadAutomobileRetireeDiscountPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "ride_sharing": {
        dispatch(
          downloadAutomobileSharingFillablePDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "garage_supplementary": {
        dispatch(
          downloadAutomobileSharingFillablePDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "cancellation_or_reinstatement": {
        dispatch(
          downloadCancellationReinstatementPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
      case "intact_authorization": {
        dispatch(
          downloadPolicyIntactAuthorizationPDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {
              setDownloading(false);
            }
          )
        );
        return;
      }
    }
  };

  const handlePreviewClick = (
    policyFormType: string,
    incremental_number: number
  ) => {
    switch (policyFormType) {
      case "liability_slips": {
        dispatch(
          previewLiabilitySlipsPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "certificate_of_liability_insurance": {
        dispatch(
          previewAutomobileLiabilityInsurancePDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "OPCF-16": {
        dispatch(
          previewAutomobileOcep16ListPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "OPCF-17": {
        dispatch(
          previewAutomobileOcep17ListPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "OPCF-48": {
        dispatch(
          previewOpcf48PDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {}
          )
        );
        return;
      }
      case "OPCF-47R": {
        dispatch(
          previewOpcf47RPDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {}
          )
        );
        return;
      }
      case "OPCF-49": {
        dispatch(
          previewOpcf49PDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {}
          )
        );
        return;
      }
      case "SEF-16": {
        dispatch(
          previewSef16PDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {}
          )
        );
        return;
      }
      case "SEF-17": {
        dispatch(
          previewSef17PDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {}
          )
        );
        return;
      }
      case "SEF-19": {
        dispatch(
          previewSef19PDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {}
          )
        );
        return;
      }
      case "SEF-13d": {
        dispatch(
          previewSef13DPDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {}
          )
        );
        return;
      }
      case "SEF-13h": {
        dispatch(
          previewSef13HPDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {}
          )
        );
        return;
      }
      case "SEF-40": {
        dispatch(
          previewSef40PDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {}
          )
        );
        return;
      }
      case "SEF-28A": {
        dispatch(
          previewAutomobileOcep28AListPDFAsync(
            customerPolicyId,
            incremental_number,
            "sef",
            () => {}
          )
        );
        return;
      }
      case "certificate_of_automobile_insurance": {
        dispatch(
          previewAutoInsurancePDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {}
          )
        );
        return;
      }
      case "OPCF-28A": {
        dispatch(
          previewAutomobileOcep28AListPDFAsync(
            customerPolicyId,
            incremental_number,
            "opcf",
            () => {}
          )
        );
        return;
      }
      case "payment_authorization": {
        dispatch(
          previewAutomobileAuthorizationPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "privacy_consent": {
        dispatch(
          previewAutomobileConsentFormPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "retiree_discount": {
        dispatch(
          previewAutomobileRetireeDiscountPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "ride_sharing": {
        dispatch(
          previewAutomobileSharingFillablePDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "garage_supplementary": {
        dispatch(
          previewAutomobileSupplementaryFormPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "cancellation_or_reinstatement": {
        dispatch(
          previewCancellationReinstatementPDFAsync(
            customerPolicyId,
            incremental_number,
            () => {}
          )
        );
        return;
      }
      case "intact_authorization": {
        dispatch(
          previewPolicyIntactAuthorizationPDFAsync(
            customerPolicyId,
            incremental_number,
            "automobile",
            () => {}
          )
        );
        return;
      }
    }
  };

  const {
    state: { columnsConfig, pagination,
      dense },
    setColumnVisibility,
    onPageChange,
    onRowsPerPageChange,
 
    onDenseToggle, } = useTableV2State({
    filtersInitialState: {
      defaultDateRange: "last90Days",
      selectedTab: "-1",
    },
    pagination: {
      pageNumber: 1,
      rowsInPerPage: 25,
      apiFetchRowCount: 26,
      extraFetchFactor: 1,
    },
  });

  const fetchList = () => {
    dispatch(
      fetchAutomobilePolicyFormsListAsync(
        pagination.pageNumber,
        pagination.rowsInPerPage,
        pagination.apiFetchRowCount,
        customerPolicyId,
        policyFormRoot,
        status,
      )
    );
  };

  const AutomobilePolicyFormDataProps: IDataTableV2Props = {
    isPagination: true,
    rowsPerPageOptions: pagination.rowsInPerPage,
    extraFetchFactor: pagination.extraFetchFactor,
    isDataLoading: loading === LoadState.InProgress,
    selectionMode: "none",
    uniqueRowKeyName: "incemental_number",
    tableCommandBarProps: {
      preDefinedPlugins: {
        columnVisibility: {
          columnVisibility: columnsConfig.columnVisibility,
          onChange: setColumnVisibility,
        },
        refresh: {
          onClick: fetchList,
        },
      },
      leftItems: {
        customPlugins: [
          {
            key: "policy_form_type",
            onRender: () => (
              <ControlledCustomSelect
                sx={{ minWidth: "120px" }}
                value={policyFormRoot}
                options={POLICY_FORM_TYPES}
                onChange={(e) => setPolicyFormRoot(e.target.value as string)}
              ></ControlledCustomSelect>
            ),
          },
          {
            key: "status",
            onRender: () => (
              <ControlledCustomSelect
                fullWidth
                value={status}
                name="status"
                onChange={(e) => setStatus(e.target.value as "ACTIVE")}
                placeholder="Select one"
                displayEmpty
                options={COMMON_STATUS_WITH_ALL}
              />
            ),
          },
        ],
      },
      rightItems: {
        customPlugins: [],
      },
    },
    masterColumns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        headingAlign: "center",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IAutomobilePolicyForm) => {
          return (
            <StandardTableActions
              downloadLoading={downloading}
              onEditClick={handleFormTypeClick(
                row.policy_form_type,
                row.incremental_number
              )}
              onDuplicateClick={handleDuplicateFormTypeClick(
                row.policy_form_type,
                row.incremental_number
              )}
              onDownLoadClick={
                row.status?.toLowerCase() === "active" &&
                DOWNLOAD_FOR.includes(row.policy_form_type)
                  ? () =>
                      handleDownloadClick(
                        row.policy_form_type,
                        row.incremental_number as any
                      )
                  : undefined
              }
              onDownloadPreview={
                row.status?.toLowerCase() === "active" &&
                PREVIEW_FOR.includes(row.policy_form_type)
                  ? () =>
                      handlePreviewClick(
                        row.policy_form_type,
                        row.incremental_number as any
                      )
                  : undefined
              }
            />
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IAutomobilePolicyForm) => {
          return <StatusRenderer status={row.status} />;
        },
      },
      {
        key: "policy_number",
        headerName: "Policy Number",
        fieldName: "policy_number",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
      },
      {
        key: "type",
        headerName: "Policy Form Type",
        fieldName: "policy_form_type",
        renderType: DataTableV2RowRenderType.CUSTOM_RENDER,
        // exportCellWidth: 30,
        onRowCellValueRender: (value, row) => {
          return formatText(value);
        },
      },
      {
        key: "create_ts",
        headerName: "Date Created",
        fieldName: "create_ts",
        renderType: DataTableV2RowRenderType.DATE,
        // exportCellWidth: 30,
      },
      {
        key: "created_by_name",
        headerName: "Created By",
        fieldName: "created_by_name",
        renderType: DataTableV2RowRenderType.TEXT_DARK,
        // exportCellWidth: 30,
      },
    ],
    initialSortConfig: {
      insert_ts: {
        direction: "desc",
        filedName: "create_ts",
      },
    },
    rows: list,
    onPageChange: onPageChange,
    onRowsPerPageChange: onRowsPerPageChange,
      dense: dense,
    onDenseToggle: onDenseToggle,};

  React.useEffect(() => {
    if (customerPolicyId) {
      fetchList();
    }
  }, [customerPolicyId, pagination, policyFormRoot, status]);

  return (
    <PageContainer title="Policy Form" description="this is inner page">
      <PageLoader
        loading={customerInfo.loading === LoadState.InProgress}
        error={customerInfo.error ? { message: customerInfo.error } : null}
      >
        <PageTitleBar
          heading="Policy Forms"
          rightHeading={
            <Stack>
              <Button
                id="demo-customized-button"
                aria-controls={open ? "demo-customized-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={open ? "true" : undefined}
                variant="contained"
                disableElevation
                onClick={handleClick}
                startIcon={<Add />}
                endIcon={<KeyboardArrowDownIcon />}
              >
                Create
              </Button>
              <StyledMenu
                id="demo-customized-menu"
                MenuListProps={{
                  "aria-labelledby": "demo-customized-button",
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
              >
                {POLICY_FORM_TYPES.slice(1).map((item, index) => {
                  return (
                    <MenuItem
                      key={index}
                      onClick={handleFormTypeClick(item.value)}
                      disableRipple
                    >
                      {item.label}
                    </MenuItem>
                  );
                })}
              </StyledMenu>
            </Stack>
          }
        />

        <DataTableV2 {...AutomobilePolicyFormDataProps} />
        {/* {openPolicyCancel && customerId && customerPolicyId && customerInfo && (
          <CancellationDialog
            customerPolicyId={customerPolicyId}
            open={openPolicyCancel}
            data={customerInfo.data}
            onClose={() => setOpenPolicyCancel(false)}
            changeNextStep={handlePolicyCancelNextStep}
          />
        )} */}
      </PageLoader>
    </PageContainer>
  );
};
